// ShuttleSelection.js
import React, { useState, useEffect } from 'react';
import '../FishFinderBuilder.css';
import { shuttlesData } from '../data/productData';
import ImageModal from './ImageModal';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 80px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;

  @media (min-width: 769px) {
    height: 100px;
  }
`;

const ProductImage = styled.img`
  width: 85%;
  height: 85%;
  object-fit: contain;

  @media (min-width: 769px) {
    width: 95%;
    height: 95%;
  }
`;

const ShuttleSelection = ({
  shuttle = null,
  setShuttle = () => {},
  prevStep = () => {},
  nextStep = () => {},
  resetAll = () => {},
  jumpToStep = () => {},
  isInModal = false,
  onNavigateToFullPage = () => {}
}) => {
  const navigate = useNavigate();
  const [failedImages, setFailedImages] = useState({});
  const [modalImage, setModalImage] = useState({
    isOpen: false,
    url: '',
    alt: ''
  });
  const [selectedShuttleId, setSelectedShuttleId] = useState(shuttle?.id || null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleImageError = (e, shuttleName) => {
    if (failedImages[shuttleName]) return;
    
    console.error(`Image failed to load for ${shuttleName}: ${e.target.src}`);
    setFailedImages(prev => ({...prev, [shuttleName]: true}));
    
    const shuttleData = shuttlesData.find(s => s.name === shuttleName);
    
    if (shuttleData && shuttleData.fallbackImage) {
      e.target.src = shuttleData.fallbackImage;
      return;
    }
    
    e.target.src = `https://dummyimage.com/150x100/cccccc/000000&text=${shuttleName.replace(/\s+/g, '+')}`;
    e.target.onerror = null;
  };

  const handleShuttleSelect = (selectedShuttle) => {
    setSelectedShuttleId(selectedShuttle.id);
    setShuttle(selectedShuttle);
  };

  return (
    <div style={{ backgroundColor: '#000000', minHeight: '100vh', padding: '0 20px' }}>
      {/* Back Button */}
      <div style={{
        padding: '15px 0',
        width: '100%',
        marginLeft: '-20px'
      }}>
        <button
          onClick={prevStep}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(0, 0, 0, 0.4)',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '8px 16px',
            marginLeft: '20px',
            borderRadius: '4px',
            width: 'fit-content'
          }}
        >
          ← Back to Builder
        </button>
      </div>

      <h1 style={{ 
        color: '#ffffff',
        fontSize: '28px',
        marginBottom: '16px'
      }}>Shuttle Selection</h1>

      <p style={{
        color: '#b8c7d9',
        fontSize: '0.95em',
        marginBottom: '24px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        padding: '12px',
        borderRadius: '4px'
      }}>
        Choose a shuttle to transport your fish finder setup easily
      </p>

      <GridContainer>
        {shuttlesData.map(shuttleOption => (
          <div key={shuttleOption.id} style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '8px',
            overflow: 'hidden',
            width: '100%'
          }}>
            <ImageContainer onClick={() => setModalImage({ isOpen: true, url: shuttleOption.image, alt: shuttleOption.name })}>
              <ProductImage 
                src={shuttleOption.image} 
                alt={shuttleOption.name}
                onError={(e) => handleImageError(e, shuttleOption.name)}
              />
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>
            </ImageContainer>

            <div style={{ padding: '8px' }}>
              <h3 style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#fff' }}>{shuttleOption.name}</h3>
              <p style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#4CAF50' }}>
                ${shuttleOption.msrp.toLocaleString()}
              </p>

              <div style={{ marginTop: '8px' }}>
                <button 
                  onClick={() => handleShuttleSelect(shuttleOption)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: selectedShuttleId === shuttleOption.id ? '#4CAF50' : 'transparent',
                    border: `1px solid ${selectedShuttleId === shuttleOption.id ? '#4CAF50' : '#555'}`,
                    borderRadius: '4px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                >
                  {selectedShuttleId === shuttleOption.id ? 'Selected ✓' : 'Select Shuttle'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </GridContainer>

      {modalImage.isOpen && (
        <ImageModal
          isOpen={modalImage.isOpen}
          imageUrl={modalImage.url}
          altText={modalImage.alt}
          onClose={() => setModalImage({ isOpen: false, url: '', alt: '' })}
        />
      )}
    </div>
  );
};

export default ShuttleSelection;
